import React, { memo, useCallback, useEffect, useId, useMemo } from 'react';
import { Platform, StyleSheet, View, } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { Reanimated3DefaultSpringConfig, withSpring, withTiming, useSharedValue, useDerivedValue, useAnimatedProps, useAnimatedStyle, } from 'react-native-reanimated';
import Svg, { Circle, Defs, G, Use } from 'react-native-svg';
import { AnimatedPath } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { triggerChartSelectionFeedback } from 'utils/hapticFeedback';
import { calculatePath, calculateProgressingPath, scaleToRect, } from 'features/invest/components/Chart/worklets/path';
import { rem } from '../values';
import Pointer from './Pointer';
import { isWeb } from '../../constants';
import MarketActivePointer from './MarketActivePointer';
const strokeWidth = Platform.OS === 'android' ? 5 : 3.32;
const shadowOpacity = Platform.OS === 'android' ? 0.2 : 0.1;
const LineChartBase = ({ width, height, strokeColor, line2StrokeColor, lineStyles, animatedProps, staticProps, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const id = useId();
    const line1Props = animatedProps
        ? { animatedProps: animatedProps.line1 }
        : staticProps?.line1 ?? {};
    const line2Props = animatedProps?.line2
        ? { animatedProps: animatedProps.line2 }
        : staticProps?.line2 ?? null;
    return (<View collapsable={false}>
      {line2Props !== null && (<Animated.View style={styles.absContainer}>
          <Animated.View style={lineStyles}>
            <Svg width={width} height={height + 20} // temporary fix for clipped chart
         style={isWeb
                ? undefined
                : [
                    styles.shadowLine2,
                    !!line2StrokeColor && {
                        shadowColor: line2StrokeColor,
                    },
                ]} viewBox={`0 0 ${width} ${height}`}>
              <Defs>
                <AnimatedPath fill="transparent" id={`chartLine2${id}`} strokeLinejoin="round" {...line2Props} stroke={line2StrokeColor || colors.elements.brandMedium}/>
              </Defs>
              <G>
                <Use x={0} y={0} href={`#chartLine2${id}`} opacity={shadowOpacity} strokeWidth={strokeWidth}/>
                <Use x={0} y={0} strokeWidth={1.66} href={`#chartLine2${id}`}/>
              </G>
            </Svg>
          </Animated.View>
        </Animated.View>)}
      <Animated.View style={isWeb ? undefined : styles.container}>
        <Animated.View style={lineStyles}>
          <Svg width={width} height={height + 20} // temporary fix for clipped chart
     style={isWeb
            ? undefined
            : [styles.shadow, !!strokeColor && { shadowColor: strokeColor }]} viewBox={`0 0 ${width} ${height}`}>
            <Defs>
              <AnimatedPath id={`chartLine${id}`} strokeLinejoin="round" {...line1Props} stroke={strokeColor || colors.elements.brand} fill="transparent"/>
            </Defs>
            <G>
              <Use x={0} y={0} href={`#chartLine${id}`} opacity={shadowOpacity} strokeWidth={strokeWidth}/>
              <Use x={0} y={0} href={`#chartLine${id}`} strokeWidth={1.66}/>
            </G>
          </Svg>
        </Animated.View>
      </Animated.View>
    </View>);
};
const POINT_CIRCLE_RADIUS = rem(5);
const PointCircle = ({ point, color, }) => <Circle cx={point.x} cy={point.y} r={POINT_CIRCLE_RADIUS} fill={color}/>;
export const StaticLineChart = ({ points, points2, width, height, strokeColor, line2StrokeColor, showEndPoint, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const inset = showEndPoint ? POINT_CIRCLE_RADIUS + 0.5 : 0;
    const W = width;
    const H = height + inset * 2;
    const chartRect = useMemo(() => ({
        width: width - inset,
        height,
    }), [width, height, inset]);
    const calculateEndPoint = useCallback((point) => {
        const { x, y } = scaleToRect(chartRect)(point);
        return { x, y: y + inset };
    }, [inset, chartRect]);
    const line1 = useMemo(() => ({
        path: calculatePath(points, chartRect),
        endPoint: calculateEndPoint(points[points.length - 1]),
    }), [calculateEndPoint, chartRect, points]);
    const line2 = useMemo(() => {
        if (!points2)
            return null;
        const path = calculatePath(points2, chartRect);
        const endPoint = calculateEndPoint(points2[points2.length - 1]);
        return {
            path,
            endPoint,
        };
    }, [points2, chartRect, calculateEndPoint]);
    return (<View style={[styles.lineChartWrapper, { height: H }]}>
      {showEndPoint === true && line2 !== null && (<View style={[styles.absContainer]}>
          <Svg width={W} height={H}>
            <PointCircle point={line2.endPoint} color={line2StrokeColor || colors.elements.brandMedium}/>
          </Svg>
        </View>)}
      <LineChartBase width={chartRect.width} height={chartRect.height} strokeColor={strokeColor} line2StrokeColor={line2StrokeColor} staticProps={{
            line1: { d: line1.path },
            line2: line2 !== null ? { d: line2.path } : undefined,
        }}/>
      {showEndPoint === true && (<View style={styles.absContainer}>
          <Svg width={W} height={H} style={isWeb !== true && [styles.shadow, { shadowColor: strokeColor }]}>
            <PointCircle point={line1.endPoint} color={strokeColor}/>
          </Svg>
        </View>)}
    </View>);
};
const LineChart = ({ width, points, points2, height, strokeColor, activePoint, combineLines, activeMarkerColor, line2StrokeColor, hasActiveMarker, selectedInfoObj, selectedInfoObj2, paddingHorizontal, chartWrapperHeight, canSelectEmptyPoints, }) => {
    const styles = useStyles(styleSet);
    const progress = useSharedValue(0);
    const isGestureActive = useSharedValue(false);
    const currentPath = useSharedValue([]);
    const currentPath2 = useSharedValue([]);
    const previousPath = useSharedValue([]);
    const previousPath2 = useSharedValue([]);
    useEffect(() => {
        if (points && (canSelectEmptyPoints || points?.length)) {
            if (currentPath.value.length === 0) {
                previousPath.value = points;
            }
            else {
                previousPath.value = currentPath.value;
            }
            if (points2 !== undefined) {
                if (currentPath2.value.length === 0) {
                    previousPath2.value = points2;
                }
                else {
                    previousPath2.value = currentPath2.value;
                }
            }
            progress.value = 0;
            currentPath.value = points;
            if (points2 !== undefined) {
                currentPath2.value = points2;
            }
            progress.value = withTiming(1);
        }
    }, [points, canSelectEmptyPoints]);
    const path = useDerivedValue(() => calculateProgressingPath(currentPath.value, previousPath.value, progress.value, {
        width,
        height,
    }), [currentPath, previousPath, progress, width, height]);
    const path2 = useDerivedValue(() => {
        if (points2 === undefined) {
            return '';
        }
        return calculateProgressingPath(currentPath2.value, previousPath2.value, progress.value, {
            width,
            height,
        });
    }, [width, height, points2, currentPath2, previousPath2, progress]);
    const animatedProps = useAnimatedProps(() => {
        const props = {
            d: path.value,
        };
        return props;
    }, [path]);
    const animatedProps2 = useAnimatedProps(() => {
        if (points2 === undefined) {
            return {
                d: '',
            };
        }
        const props = {
            d: path2.value,
        };
        return props;
    }, [points2, path2]);
    const opacityStyles = useAnimatedStyle(() => ({
        opacity: withSpring(!selectedInfoObj.value ? 1 : 0.5, Reanimated3DefaultSpringConfig),
    }), [selectedInfoObj]);
    const onEnd = useCallback(() => {
        'worklet';
        isGestureActive.value = false;
        // eslint-disable-next-line no-param-reassign
        selectedInfoObj.value = undefined;
        if (selectedInfoObj2) {
            // eslint-disable-next-line no-param-reassign
            selectedInfoObj2.value = undefined;
        }
    }, [isGestureActive, selectedInfoObj, selectedInfoObj2]);
    const onChange = useCallback((event) => {
        'worklet';
        if (!isGestureActive.value) {
            triggerChartSelectionFeedback();
        }
        isGestureActive.value = true;
        if (!currentPath.value.length) {
            // eslint-disable-next-line no-param-reassign
            selectedInfoObj.value = undefined;
            return;
        }
        const computedX = (event.x - (paddingHorizontal || 0)) / width;
        const { index, pathToUse } = (() => {
            if (!combineLines || !points2?.length) {
                return {
                    index: computedX > 1
                        ? currentPath.value.length - 1
                        : currentPath.value.findIndex((point) => point.x >= computedX),
                    pathToUse: currentPath,
                };
            }
            if (computedX > 1) {
                return {
                    index: currentPath2.value.length - 1,
                    pathToUse: currentPath2,
                };
            }
            return computedX >= currentPath2.value[0].x
                ? {
                    index: currentPath2.value.findIndex((point) => point.x >= computedX),
                    pathToUse: currentPath2,
                }
                : {
                    index: currentPath.value.findIndex((point) => point.x >= computedX),
                    pathToUse: currentPath,
                };
        })();
        if (index > 0) {
            const updated = {
                ...pathToUse.value[index],
                x: (pathToUse.value[index]?.x || 0) * width,
                y: (pathToUse.value[index]?.y || 0) * height + 10,
            };
            // eslint-disable-next-line no-param-reassign
            selectedInfoObj.value = updated;
            if (points2?.length && selectedInfoObj2 && !combineLines) {
                const updated2 = {
                    ...currentPath2.value[index],
                    x: (currentPath2.value[index]?.x || 0) * width,
                    y: (currentPath2.value[index]?.y || 0) * height + 10,
                };
                // eslint-disable-next-line no-param-reassign
                selectedInfoObj2.value = updated2;
            }
        }
        else if (index === -1) {
            const currentPathLen = pathToUse.value.length;
            const updated = {
                ...pathToUse.value[currentPathLen - 1],
                x: (pathToUse.value[currentPathLen - 1]?.x || 0) * width,
                y: (pathToUse.value[currentPathLen - 1]?.y || 0) * height + 10,
            };
            // eslint-disable-next-line no-param-reassign
            selectedInfoObj.value = updated;
        }
        else {
            const updated = {
                ...pathToUse.value[0],
                x: (pathToUse.value[0]?.x || 0) * width,
                y: (pathToUse.value[0]?.y || 0) * height + 10,
            };
            // eslint-disable-next-line no-param-reassign
            selectedInfoObj.value = updated;
            if (points2?.length && selectedInfoObj2 && !combineLines) {
                const updated2 = {
                    ...currentPath2.value[0],
                    x: (currentPath2.value[0]?.x || 0) * width,
                    y: (currentPath2.value[0]?.y || 0) * height + 10,
                };
                // eslint-disable-next-line no-param-reassign
                selectedInfoObj2.value = updated2;
            }
        }
    }, [
        combineLines,
        currentPath,
        currentPath2,
        height,
        isGestureActive,
        paddingHorizontal,
        points2,
        selectedInfoObj,
        selectedInfoObj2,
        width,
    ]);
    const onDrag = useMemo(() => Gesture.Simultaneous(Gesture.Pan().onChange(onChange).onEnd(onEnd), Gesture.LongPress()
        .onBegin(onChange)
        .onStart(onChange)
        .onTouchesCancelled(onEnd)
        .onEnd(onEnd)
        .onTouchesCancelled(onEnd)
        .minDuration(0)
        .maxDistance(100000)
        .shouldCancelWhenOutside(false)), [onChange, onEnd]);
    return (<View style={[styles.lineChartWrapper, { height: chartWrapperHeight }]}>
      <View style={{ height: height + 20 }}>
        <GestureDetector gesture={onDrag}>
          <LineChartBase width={width} height={height} strokeColor={strokeColor} line2StrokeColor={line2StrokeColor} animatedProps={{
            line1: animatedProps,
            line2: points2 !== undefined ? animatedProps2 : undefined,
        }} lineStyles={opacityStyles}/>
        </GestureDetector>
        {hasActiveMarker ? (<MarketActivePointer width={width} height={height} color={activeMarkerColor} finalPoint={activePoint}/>) : null}
        <Pointer height={height + 20} selectedInfoObj={selectedInfoObj}/>
      </View>
    </View>);
};
export default memo(LineChart);
const styleSet = createStyleSheets((colors) => ({
    container: {
        shadowRadius: 2,
        shadowOpacity: 0.5,
        shadowColor: colors.shadows.chart,
        shadowOffset: { width: 3, height: 4 },
    },
    absContainer: {
        ...StyleSheet.absoluteFillObject,
    },
    lineChartWrapper: {
        justifyContent: 'center',
    },
    shadow: {
        shadowRadius: 5,
        shadowOpacity: 0.5,
        shadowColor: colors.elements.brand,
        shadowOffset: { width: 0, height: 0 },
    },
    shadowLine2: {
        shadowRadius: 5,
        shadowOpacity: 0.5,
        shadowColor: colors.elements.brand,
        shadowOffset: { width: 0, height: 0 },
    },
}));
